import { Link, useNavigate } from 'react-router'
import { Building2, FileStack } from 'lucide-react'
import { STAGE_STATUSES, type StageStatus } from '@concours/shared'
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Empty,
  PageHeader,
  Table,
  usePermissions,
  type Column,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { useDocumentsHub, type HubStage } from '../../lib/documents'

/**
 * Convocations et documents officiels (cahier §9.2, §10) : par concours, les
 * centres d'examen et, par étape, les convocations et documents produits.
 */
export function DocumentsHubPage() {
  const navigate = useNavigate()
  const hub = useDocumentsHub()
  const { can } = usePermissions()
  const canCenters = (id: string) =>
    can('centers.manage', { type: 'competition', id }) ||
    can('convocations.generate', { type: 'competition', id })

  const columns: Column<HubStage>[] = [
    { key: 'stage', header: 'Étape', cell: (s) => `${s.position}. ${s.label}` },
    {
      key: 'status',
      header: 'Statut',
      cell: (s) => STAGE_STATUSES[s.status as StageStatus] ?? s.status,
    },
    { key: 'date', header: 'Date prévue', cell: (s) => when(s.startsAt) },
    {
      key: 'convocations',
      header: 'Convocations',
      cell: (s) =>
        s.issued || s.draft ? (
          <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {s.issued > 0 && <Badge variant="success">{s.issued} envoyée(s)</Badge>}
            {s.draft > 0 && <Badge variant="neutral">{s.draft} préparée(s)</Badge>}
          </span>
        ) : (
          <span className="muted">Aucune</span>
        ),
    },
    { key: 'documents', header: 'Documents', numeric: true, cell: (s) => s.documents || '—' },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Documents"
        title="Convocations et documents officiels"
        description="Centres et salles d’examen, répartition des candidats, convocations, listes d’émargement, procès-verbaux et listes de résultats — chaque document porte un code de vérification."
      />
      {hub.isError && <Alert tone="danger">{errorMessage(hub.error)}</Alert>}
      {hub.isPending && (
        <Card>
          <CardBody>Chargement…</CardBody>
        </Card>
      )}
      {hub.data?.length === 0 && (
        <Card>
          <Empty
            icon={<FileStack aria-hidden="true" />}
            title="Aucun concours"
            description="Les concours publiés de votre périmètre apparaissent ici."
          />
        </Card>
      )}
      <div className="stack" style={{ gap: 'var(--space-6)' }}>
        {hub.data?.map((c) => (
          <Card key={c.id}>
            <CardHeader
              title={
                <span className="stack" style={{ gap: 2 }}>
                  <span>{c.title}</span>
                  <span className="mono muted" style={{ fontSize: 'var(--text-sm)' }}>
                    {c.reference}
                    {c.session ? ` · ${c.session}` : ''}
                  </span>
                </span>
              }
              actions={
                canCenters(c.id) && (
                  <Link className="btn btn-secondary btn-sm" to={`/documents/concours/${c.id}`}>
                    <Building2 aria-hidden="true" size={16} />
                    Centres et salles
                    <span className="muted">
                      ({c.centers} · {c.capacity} places)
                    </span>
                  </Link>
                )
              }
            />
            <Table
              columns={columns}
              rows={c.stages}
              rowKey={(s) => s.id}
              caption={`Étapes — ${c.title}`}
              onRowClick={(s) => navigate(`/documents/etapes/${s.id}`)}
              empty={
                <Empty
                  title="Aucune étape configurée"
                  description="Les étapes se configurent dans le moteur de sélection."
                />
              }
            />
          </Card>
        ))}
      </div>
    </>
  )
}
